import React, { useState, useEffect } from 'react';
import { useAuth } from '../../../contexts/AuthContext';
import Input from '../../../components/ui/Input';
import Button from '../../../components/ui/Button';
import Select from '../../../components/ui/Select';
import { Checkbox } from '../../../components/ui/Checkbox';
import Icon from '../../../components/AppIcon';

const AttendeeInfoForm = ({ onNext, formData, setFormData }) => {
  const { user } = useAuth();
  const [errors, setErrors] = useState({});

  // Prefill demo banner state
  const [showDemoNotice, setShowDemoNotice] = useState(true);

  // Initialize form with sample data on mount if form is empty
  useEffect(() => {
    if (!formData?.firstName && !formData?.lastName && !formData?.email) {
      const sampleData = {
        firstName: user?.first_name || 'John',
        lastName: user?.last_name || 'Smith',
        email: user?.email || 'john.smith@example.com',
        phone: user?.phone || '+1 (555) 123-4567',
        address: '123 Main Street',
        city: 'San Francisco',
        state: 'CA',
        zipCode: '94102',
        country: 'us',
        company: 'Tech Solutions Inc.',
        specialRequirements: 'Vegetarian meal preference',
        eventUpdates: true,
        marketingEmails: false,
        agreeToTerms: false
      };
      
      setFormData(prev => ({
        ...prev,
        ...sampleData
      }));
    }
  }, [formData, setFormData, user]);

  const countryOptions = [
    { value: 'us', label: 'United States' },
    { value: 'uk', label: 'United Kingdom' },
    { value: 'ca', label: 'Canada' },
    { value: 'au', label: 'Australia' },
    { value: 'de', label: 'Germany' },
    { value: 'fr', label: 'France' },
    { value: 'in', label: 'India' },
    { value: 'jp', label: 'Japan' }
  ];

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    
    // Clear error when user starts typing
    if (errors?.[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData?.firstName?.trim()) {
      newErrors.firstName = 'First name is required';
    }

    if (!formData?.lastName?.trim()) {
      newErrors.lastName = 'Last name is required';
    }

    if (!formData?.email?.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/?.test(formData?.email)) {
      newErrors.email = 'Please enter a valid email address';
    }

    if (!formData?.phone?.trim()) {
      newErrors.phone = 'Phone number is required';
    }

    if (!formData?.country) {
      newErrors.country = 'Please select your country';
    }

    // Enhanced validation for address fields based on country selection
    if (formData?.country === 'in') {
      if (!formData?.address?.trim()) {
        newErrors.address = 'Street address is required for Indian transactions per export regulations';
      }
      if (!formData?.city?.trim()) {
        newErrors.city = 'City is required for Indian transactions per export regulations';
      }
    }

    if (!formData?.agreeToTerms) {
      newErrors.agreeToTerms = 'You must agree to the terms and conditions';
    }

    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleSubmit = (e) => {
    e?.preventDefault();
    if (validateForm()) {
      onNext();
    }
  };

  // Check if selected country requires enhanced address information
  const requiresEnhancedAddress = formData?.country === 'in';

  return (
    <div className="bg-background border border-border rounded-lg p-6">
      {/* Demo Notice Banner */}
      {showDemoNotice && (
        <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
          <div className="flex items-start justify-between">
            <div className="flex items-start space-x-3">
              <Icon name="Info" size={20} className="text-blue-600 mt-0.5" />
              <div>
                <h3 className="text-sm font-medium text-blue-800 mb-1">Demo Form - Sample Data Prefilled</h3>
                <p className="text-sm text-blue-700">
                  This form has been prefilled with sample data to demonstrate the checkout experience. 
                  You can modify any field or proceed with the demo data.
                  {user && (
                    <span className="block mt-1">
                      <strong>Authenticated as:</strong> {user?.full_name || user?.email}
                    </span>
                  )}
                </p>
              </div>
            </div>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => setShowDemoNotice(false)}
            >
              <Icon name="X" size={16} />
            </Button>
          </div>
        </div>
      )}

      <div className="flex items-center space-x-3 mb-6">
        <Icon name="User" size={24} className="text-primary" />
        <div>
          <h2 className="text-xl font-semibold text-foreground">Attendee Information</h2>
          <p className="text-sm text-muted-foreground">Please provide your details for the event registration</p>
        </div>
      </div>

      {requiresEnhancedAddress && (
        <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
          <div className="flex items-start space-x-3">
            <Icon name="Info" size={20} className="text-blue-600 mt-0.5" />
            <div>
              <h3 className="text-sm font-medium text-blue-800 mb-1">Address Required for Compliance</h3>
              <p className="text-sm text-blue-700">
                For transactions from India, complete address information is required by export regulations.
                <br />
                <strong>Note:</strong> If you cannot provide a complete address, basic placeholder information will be used to ensure payment compliance while protecting your privacy.
              </p>
            </div>
          </div>
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Personal Information */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Input
            label="First Name"
            type="text"
            placeholder="Enter your first name"
            value={formData?.firstName || ''}
            onChange={(e) => handleInputChange('firstName', e?.target?.value)}
            error={errors?.firstName}
            required
          />
          
          <Input
            label="Last Name"
            type="text"
            placeholder="Enter your last name"
            value={formData?.lastName || ''}
            onChange={(e) => handleInputChange('lastName', e?.target?.value)}
            error={errors?.lastName}
            required
          />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Input
            label="Email Address"
            type="email"
            placeholder="Enter your email"
            value={formData?.email || ''}
            onChange={(e) => handleInputChange('email', e?.target?.value)}
            error={errors?.email}
            description="We'll send your tickets and updates to this email"
            required
          />
          
          <Input
            label="Phone Number"
            type="tel"
            placeholder="Enter your phone number"
            value={formData?.phone || ''}
            onChange={(e) => handleInputChange('phone', e?.target?.value)}
            error={errors?.phone}
            required
          />
        </div>

        {/* Address Information */}
        <div className="space-y-4">
          <h3 className="text-lg font-medium text-foreground">Address Information</h3>
          
          <Select
            label="Country"
            placeholder="Select your country"
            options={countryOptions}
            value={formData?.country || ''}
            onChange={(value) => handleInputChange('country', value)}
            error={errors?.country}
            required
            searchable
            className="max-w-md"
          />

          <Input
            label={`Street Address ${requiresEnhancedAddress ? '(Required)' : '(Optional)'}`}
            type="text"
            placeholder="Enter your street address"
            value={formData?.address || ''}
            onChange={(e) => handleInputChange('address', e?.target?.value)}
            error={errors?.address}
            required={requiresEnhancedAddress}
          />
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Input
              label={`City ${requiresEnhancedAddress ? '(Required)' : '(Optional)'}`}
              type="text"
              placeholder="Enter city"
              value={formData?.city || ''}
              onChange={(e) => handleInputChange('city', e?.target?.value)}
              error={errors?.city}
              required={requiresEnhancedAddress}
            />
            
            <Input
              label="State/Province (Optional)"
              type="text"
              placeholder="Enter state"
              value={formData?.state || ''}
              onChange={(e) => handleInputChange('state', e?.target?.value)}
            />
            
            <Input
              label="ZIP/Postal Code (Optional)"
              type="text"
              placeholder="Enter ZIP code"
              value={formData?.zipCode || ''}
              onChange={(e) => handleInputChange('zipCode', e?.target?.value)}
            />
          </div>
        </div>

        {/* Optional Information */}
        <div className="space-y-4">
          <h3 className="text-lg font-medium text-foreground">Additional Information</h3>
          
          <Input
            label="Company/Organization (Optional)"
            type="text"
            placeholder="Enter your company name"
            value={formData?.company || ''}
            onChange={(e) => handleInputChange('company', e?.target?.value)}
          />
          
          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              Special Requirements or Dietary Restrictions (Optional)
            </label>
            <textarea
              className="w-full px-3 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-ring focus:border-transparent resize-none"
              rows={3}
              placeholder="Please let us know about any special requirements..."
              value={formData?.specialRequirements || ''}
              onChange={(e) => handleInputChange('specialRequirements', e?.target?.value)}
            />
          </div>
        </div>

        {/* Marketing Preferences */}
        <div className="space-y-3 p-4 bg-muted rounded-lg">
          <h3 className="text-sm font-medium text-foreground">Communication Preferences</h3>
          
          <Checkbox
            label="Send me updates about this event"
            checked={formData?.eventUpdates || false}
            onChange={(e) => handleInputChange('eventUpdates', e?.target?.checked)}
          />
          
          <Checkbox
            label="Send me information about similar events"
            checked={formData?.marketingEmails || false}
            onChange={(e) => handleInputChange('marketingEmails', e?.target?.checked)}
          />
        </div>

        {/* Terms and Conditions */}
        <div className="space-y-3">
          <Checkbox
            label="I agree to the Terms and Conditions and Privacy Policy"
            checked={formData?.agreeToTerms || false}
            onChange={(e) => handleInputChange('agreeToTerms', e?.target?.checked)}
            error={errors?.agreeToTerms}
            required
          />
        </div>

        {/* Action Buttons */}
        <div className="flex flex-col sm:flex-row gap-3 pt-6 border-t border-border">
          <Button
            type="button"
            variant="outline"
            onClick={() => window.history?.back()}
            className="sm:w-auto"
          >
            <Icon name="ArrowLeft" size={16} className="mr-2" />
            Back to Event
          </Button>
          
          <Button
            type="submit"
            variant="default"
            className="sm:ml-auto sm:w-auto"
          >
            Continue to Payment
            <Icon name="ArrowRight" size={16} className="ml-2" />
          </Button>
        </div>
      </form>
    </div>
  );
};

export default AttendeeInfoForm;